import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { translate } from '../../locales';
import { useTheme } from '@mui/material/styles';
import Search from '../SelectMenuUtils/Search';
import LoadingMessage from '../SelectMenuUtils/LoadingMessage';
import Menu from '../SelectMenuUtils/Menu';
import createOption from '../SelectMenuUtils/createOption';
import MultiOption from '../SelectMenuUtils/MultiOption';
import NoOptionsWithAddNew from '../SelectMenuUtils/NoOptionsWithAddNew';
import { ITEMS_AMOUNT_BEFORE_DISPLAYING_SEARCH } from '../SelectMenuUtils/consts';
import {
  countSelection,
  sortOptions,
  sortValues,
  shouldCreateNewOption
} from './utils';
import { reducer, initReducer, defaultReducerState } from './reducer';
import { createFilterOptions } from './useAutoComplete/useAutocomplete';
import BaseSelectMenu from '../SelectMenuUtils/BaseSelectMenu/BaseSelectMenu';
import ExtraInfo from '../SelectMenuUtils/ExtraInfo';
import { multiSelectMenuClasses as classes } from './multiSelectMenuClasses';
import {
  MultiSelectMenuRoot,
  MultiSelectMenuContent
} from './MultiSelectMenu.style';

const MultiSelectMenu = forwardRef(
  (
    {
      isOpen,
      setIsOpen,
      anchorRef,
      options,
      values,
      onChange,
      onSelection,
      onInputChange = () => {},
      placeholder = translate('common.searchPlaceholder'),
      maxAmountOfVisibleOptionsToShowSelectAll = 0,
      renderOption = MultiOption,
      maxOptions = 200,
      size = 'medium',
      isLoading = false,
      /** option => true|false */
      isOptionDisabled = () => false,
      async = false,
      creatable = false,
      AddButtonProps,
      onCreate,
      headerElement,
      customNoResult,
      getFilterKey = option => [option.label, option.groupName],
      maxValues = Infinity,
      PopoverProps = {},
      renderExtraInfo,
      isGroupsCollapsedByDefault,
      showSelectionCounter = true,
      onSelectAllClick: incomingOnSelectAllClick,
      onKeyDown: incomingOnKeyDown,
      shouldCreateNewOption: incomingShouldCreateNewOption,
      className,
      sx,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const isFirstRenderRef = useRef(true);

    const filterOptions = createFilterOptions({
      limit: maxOptions
    });
    const [focusedItem, setFocusedItem] = useState(options[0]);
    const [state, dispatch] = useReducer(
      reducer,
      defaultReducerState,
      initialState => initReducer(initialState, { options, values })
    );

    const filteredOptions = filterOptions(sortOptions({ options, values }), {
      inputValue: state.inputValue,
      getFilterKey
    });

    const filteredValues = filterOptions(sortValues({ values }), {
      inputValue: state.inputValue,
      getFilterKey
    });

    const counterSelection = showSelectionCounter
      ? countSelection(state.selectedOptions, maxValues)
      : null;

    const [, forceReRender] = useReducer(x => !x, false);
    const { current: anchorElement } = anchorRef;
    const containerWidth = useMemo(() => {
      return anchorElement?.getBoundingClientRect?.()?.width;
    }, [anchorElement]);

    const searchInputRef = useRef(null);
    const isSearchable =
      options.length > ITEMS_AMOUNT_BEFORE_DISPLAYING_SEARCH || async;
    const components = {
      NoOptionsMessage: function NoOptionsMessage(props) {
        return (
          <NoOptionsWithAddNew
            {...props}
            customNoResult={customNoResult}
            sizeValue={Math.max(
              theme.sizes?.multiSelectMenu[size],
              theme.sizes?.multiSelectMenu.medium
            )}
          />
        );
      },
      Menu,
      Option: renderOption,
      LoadingMessage,
      Search: isSearchable ? Search : React.Fragment
    };

    useEffect(() => {
      // we don't want to trigger onSelection on initial render
      if (isFirstRenderRef.current) {
        isFirstRenderRef.current = false;
        return;
      }
      if (onSelection) {
        onSelection(state.selectedOptions);
      }

      // consumers must provide a stable reference of onSelection, otherwise it will trigger onSelection on each render.
    }, [state.selectedOptions, onSelection]);

    useLayoutEffect(() => {
      // we are forcing a re-render in case isOpen is passed as true on initial render.
      // we will need to trigger an extra render in order to pass the calculated width of the anchor (especially in fullWidth mode).
      // Future note: We can't use resizeObserver because currently safari supports only the contentRect (no border and padding) and not the boxSizeRect.
      forceReRender();
    }, []);

    useEffect(() => {
      // sync values from parent with local selectedOptions
      dispatch({
        type: 'setSelectedOptions',
        payload: { selectedOptions: values }
      });
      // when values change (due to applying changes/closing menu) we need to reset range selection
      resetStartOfSelectionRange();
    }, [values]);

    const handleCloseAndApplyChanges = useCallback(
      e => {
        if (e.key === 'Escape') {
          return;
        }

        setIsOpen(false);
        onChange(state.selectedOptions);
      },
      [state.selectedOptions, onChange, setIsOpen]
    );

    const handleCloseAndDiscardChanges = useCallback(() => {
      dispatch({
        type: 'setSelectedOptions',
        payload: { selectedOptions: values }
      });
      setIsOpen(false);
    }, [setIsOpen, values]);

    const resetStartOfSelectionRange = () =>
      dispatch({
        type: 'resetStartOfSelectionRange'
      });

    const onSelect = useCallback(
      (currentClickedOption, e) => {
        if (currentClickedOption.isGroup) {
          dispatch({
            type: 'selectGroup',
            payload: {
              filteredOptions,
              clickedGroupItem: currentClickedOption
            }
          });
        } else {
          dispatch({
            type: 'onSelect',
            payload: {
              values,
              maxValues,
              options,
              filteredOptions,
              currentClickedOption,
              isOptionDisabled,
              isShift: e.shiftKey
            }
          });
        }
      },
      [values, maxValues, filteredOptions, options, isOptionDisabled]
    );

    const focusOnSelect = () => {
      searchInputRef &&
        searchInputRef.current &&
        searchInputRef.current.focus();
    };

    const handleChangeCreateNew = input => {
      const shouldCreateOptionValidationFunc =
        incomingShouldCreateNewOption || shouldCreateNewOption;

      if (!shouldCreateOptionValidationFunc(input, options)) {
        focusOnSelect();
        return;
      }

      const newOption = createOption(input);

      onCreate(newOption);
      dispatch({
        type: 'onCreateNewOption',
        payload: { newOption }
      });
      focusOnSelect();
    };

    const handleKeyDown = useCallback(
      (e, selectedOption) => {
        if (incomingOnKeyDown) {
          incomingOnKeyDown(e);
        }

        if (isOpen && e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          handleCloseAndApplyChanges(e);
        } else if (isOpen && e.key === 'Escape') {
          e.preventDefault();
          handleCloseAndDiscardChanges();
        } else if (e.key === ' ') {
          onSelect(selectedOption, e);
        }
      },
      [
        isOpen,
        handleCloseAndApplyChanges,
        handleCloseAndDiscardChanges,
        onSelect,
        incomingOnKeyDown
      ]
    );

    const {
      transitionDuration = {},
      anchorOrigin = {},
      transformOrigin = {},
      TransitionProps = {},
      PaperProps = {},
      ...restOfPopoverProps
    } = PopoverProps;

    return (
      <MultiSelectMenuRoot
        transitionDuration={{
          appear: 350,
          enter: 350,
          exit: 0, // prevent the user from seeing select updates during transition
          ...transitionDuration
        }}
        open={isOpen}
        onClose={handleCloseAndApplyChanges}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
          ...anchorOrigin
        }}
        transformOrigin={{
          vertical: -4,
          horizontal: 'left',
          ...transformOrigin
        }}
        TransitionProps={{
          onExited: (...args) => {
            dispatch({ type: 'setInputValue', payload: { inputValue: '' } });
            if (TransitionProps.onExited) {
              TransitionProps.onExited(...args);
            }
          },
          ...TransitionProps
        }}
        slotProps={{
          paper: {
            'data-qa-id': `multi-select-menu__popover`,
            elevation: 4,
            variant: 'elevation',
            className,
            sx,
            ref,
            ...PaperProps
          }
        }}
        {...restOfPopoverProps}>
        <MultiSelectMenuContent
          width={containerWidth}
          size={size}
          className={classes.multiSelectWrapper}>
          {headerElement}
          <BaseSelectMenu
            isMultiSelectionMode
            hasMenuHeader
            isGroupsCollapsedByDefault={isGroupsCollapsedByDefault}
            values={state.selectedOptions}
            preSelectedValues={filteredValues}
            options={filteredOptions}
            isSearchable={isSearchable}
            components={components}
            isLoading={isLoading}
            isOptionDisabled={isOptionDisabled}
            onKeyDown={handleKeyDown}
            onClick={onSelect}
            menuIsOpen={isOpen}
            lastSelected={filteredValues[filteredValues.length - 1]}
            maxValues={maxValues}
            onFocusedItemChange={renderExtraInfo ? setFocusedItem : undefined}
            SearchInputProps={{
              ref: searchInputRef,
              placeholder,
              value: state.inputValue,
              onChange: ({ target }) => {
                dispatch({
                  type: 'setInputValue',
                  payload: {
                    inputValue: target.value
                  }
                });
                onInputChange(target.value);
              },
              onKeyDown: handleKeyDown
            }}
            MenuProps={{
              countSelectionText: counterSelection,
              onSelectAllClick: event => {
                if (incomingOnSelectAllClick) {
                  incomingOnSelectAllClick(event);
                }

                dispatch({
                  type: 'selectAll',
                  payload: {
                    options: filteredOptions
                  }
                });
              },
              onKeyDown: handleKeyDown,
              selectedOptions: state.selectedOptions,
              // in case we limit amount of selections, we don't want to display the Select All option
              maxAmountOfVisibleOptionsToShowSelectAll: Math.min(
                maxAmountOfVisibleOptionsToShowSelectAll,
                maxValues
              ),
              creatable,
              onCreate,
              AddButtonProps,
              onCreateNew: handleChangeCreateNew,
              focusOnSelect
            }}
            {...rest}
          />
        </MultiSelectMenuContent>
        {renderExtraInfo && (
          <ExtraInfo className={classes.extraInfoRoot}>
            {renderExtraInfo(focusedItem)}
          </ExtraInfo>
        )}
      </MultiSelectMenuRoot>
    );
  }
);

MultiSelectMenu.propTypes = {
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
  anchorRef: PropTypes.any,
  values: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
      /** Support groups */
      groupName: PropTypes.string
    })
  ),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
      /** Support groups */
      groupName: PropTypes.string
    })
  ),
  onChange: PropTypes.func.isRequired,
  onSelection: PropTypes.func,
  onInputChange: PropTypes.func,
  onSelectAllClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.string,
  maxAmountOfVisibleOptionsToShowSelectAll: PropTypes.number,
  renderOption: PropTypes.elementType,
  /** renders an extra info box based on focused or hovered item
   * focusedItem => JSX
   */
  renderExtraInfo: PropTypes.elementType,
  maxOptions: PropTypes.number,
  maxValues: PropTypes.number,
  isLoading: PropTypes.bool,
  isOptionDisabled: PropTypes.func,
  async: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'fullWidth']),
  creatable: PropTypes.bool,
  /** Props that are passed to the Add new item Button component */
  AddButtonProps: PropTypes.object,
  onCreate: PropTypes.func,
  headerElement: PropTypes.element,
  customNoResult: PropTypes.node,
  /** A function to determine what key should be used when filtering the options.
   * Note: we can also return an array of keys to perform an OR match against them.
   *
   * (option: object) => string | array
   */
  getFilterKey: PropTypes.func,
  /** Props passed to the underline MUI Popover component */
  PopoverProps: PropTypes.object,
  /** Start with expended groups by default */
  isGroupsCollapsedByDefault: PropTypes.bool,
  /** A boolean to determine if the options' arrangement should be reversed, default is groups first */
  reverseOptionsOrder: PropTypes.bool,
  /** A boolean to determine if the selected options' counter should be displayed */
  showSelectionCounter: PropTypes.bool,
  /** A boolean to determine if a group options' counter should be displayed */
  showGroupCounter: PropTypes.bool,
  /** A function to determine if a new option should be created
   * (input, options) => boolean
   */
  shouldCreateNewOption: PropTypes.func
};

MultiSelectMenu.classes = classes;
MultiSelectMenu.displayName = 'MultiSelectMenu';

export default MultiSelectMenu;
